import { libelleSchemaDeGarde, type SchemaDeGarde } from '@repere/core';
import type { AvatarEnfant } from './Avatar';
/** Formes partagées par les écrans d'un dossier. Alignées sur le contrat de l'API. */

export interface ParentDuDossier {
  slot: number;
  displayName: string;
  isViewer: boolean;
  /**
   * L'adresse du compte — rendue aux parents seulement, `null` pour un professionnel.
   *
   * Facultative dans le type : une réponse d'API plus ancienne, ou un test qui ne la pose
   * pas, ne doit pas casser un écran qui n'en dépend pas.
   */
  email?: string | null;
}

export interface EtatDossier {
  mode: 'solo' | 'shared';
  messagingEnabled: boolean;
  activeParents: number;
  pendingInvitation: boolean;
}

/** Un enfant du dossier, tel que l'API le rend. */
export interface EnfantDuDossier {
  id: string;
  firstName: string;
  birthDate: string;
  avatar: AvatarEnfant;
}

/**
 * La suppression en cours d'un dossier, telle que l'API la rend.
 *
 * `pending` : demandée, en attente d'une validation par e-mail — le dossier fonctionne
 * normalement. `archived` : validée — le dossier reste consultable jusqu'à `purgeAt`, mais
 * plus personne n'y écrit. Aucun autre état ne remonte : un refus ou une annulation ne sont
 * plus un état du dossier, seulement une page de son histoire.
 */
export interface SuppressionDossier {
  id: string;
  caseFileId: string;
  status: 'pending' | 'archived';
  requestedAt: string;
  expiresAt: string;
  purgeAt: string | null;
  requestedByViewer: boolean;
  awaitingViewer: boolean;
}

export interface Dossier {
  id: string;
  label: string | null;
  children: EnfantDuDossier[];
  parents: ParentDuDossier[];
  state: EtatDossier;
  custodyKind: SchemaDeGarde | null;
  deletion: SuppressionDossier | null;
}

export interface Occurrence {
  startsAt: string;
  endsAt: string;
  parentSlot: number;
  source: 'schedule' | 'exception';
  /** Motif de la dérogation, tel qu'il a été écrit. Absent si elle n'en portait pas. */
  reason?: string | null;
  /** Type de la proposition d'origine — « day_swap » pour un échange de jours. */
  origin?: string | null;
}

export type StatutProposition = 'pending' | 'accepted' | 'declined' | 'countered' | 'expired';

export interface Proposition {
  id: string;
  type: 'schedule_change' | 'day_swap' | 'exception' | 'split_rule_change' | 'expense_dispute';
  status: StatutProposition;
  payload: Record<string, unknown>;
  reason: string | null;
  createdAt: string;
  respondedAt: string | null;
  authorId: string | null;
  repliesToId: string | null;
}

export interface VersionSchema {
  id: string;
  version: number;
  kind: string;
  params: Record<string, unknown>;
  validFrom: string;
  supersededAt: string | null;
  createdByProposalId: string | null;
}

/**
 * Libellés des statuts de proposition.
 *
 * Aucun n'attribue une action à une personne : « Déclinée », jamais « Refusée par
 * Dominique ». La formulation factuelle est une contrainte de produit, pas une
 * préférence de style — nommer l'auteur d'un refus transforme l'historique en dossier
 * à charge (docs/01 §5.5).
 */
export const LIBELLE_STATUT: Record<StatutProposition, string> = {
  pending: 'En attente de réponse',
  accepted: 'Acceptée',
  declined: 'Déclinée',
  countered: 'Contre-proposition envoyée',
  expired: 'Échue',
};

/** Libellés des types de proposition, du point de vue de ce qui est demandé. */
export const LIBELLE_TYPE: Record<Proposition['type'], string> = {
  schedule_change: 'Changement de schéma de garde',
  day_swap: 'Échange de jours',
  exception: 'Dérogation ponctuelle',
  split_rule_change: 'Changement de clé de répartition',
  expense_dispute: 'Contestation de dépense',
};

/**
 * Libellé d'un schéma de garde. Les libellés viennent de `@repere/core` : le web, l'API
 * et le simulateur du site nomment ainsi les mêmes schémas de la même façon.
 */
export const libelleSchema = libelleSchemaDeGarde;
